import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import OnSiteHelpers from '../services/helpers/OnSiteHelpers';
import OrderHelpers from '../services/helpers/OrderHelpers';

// Orders already reported this session: every screen labels the same order on each render.
const reportedOrders = new Set();

/**
 * A table order the backend sent without a table: staff can't tell where to bring it. Reported
 * once per order, under one Sentry issue.
 */
function reportMissingTable(order) {
  const key = order?.id ?? order?.number ?? 'unknown';

  if (reportedOrders.has(key)) {
    return;
  }

  reportedOrders.add(key);
  Sentry.captureMessage('Table order without a table name', {
    level: 'warning',
    fingerprint: ['table-order-without-table-name'],
    tags: { order_method: order?.method },
    extra: {
      order_id: order?.id,
      on_site_table_name: order?.on_site_table_name,
      on_site_table: order?.on_site_table,
      room_service_details: order?.room_service_details,
    },
  });
}

// Tests only: forget which orders were reported.
export const resetReportedMissingTables = () => reportedOrders.clear();

/**
 * The table of a table order as staff are told it ("Table 7", "Terrace 2"), or null.
 * Pass `lng` to label in another language than the UI's (the receipt prints in the company's).
 */
export default function useTableLabel({ lng } = {}) {
  const { t } = useTranslation();

  return useCallback(
    (order) => {
      const label = OnSiteHelpers.orderLabel(order, (key, options) => t(key, { lng, ...options }));

      if (!label && OrderHelpers.isOnSite(order) && !order.is_dummy) {
        reportMissingTable(order);
      }

      return label;
    },
    [t, lng],
  );
}
